import * as React from 'react'

import { styled } from '~/styled'

import { Tabs } from '../tabs/Tabs'
import { SegmentedControlContext } from './SegmentedControlContext'
import { SegmentedControlItem } from './SegmentedControlItem'

const StyledTriggerList = styled(Tabs.TriggerList, {
  base: [
    'relative',
    'p-1',
    'rounded-lg',
    'overflow-hidden',
    '[&>div[role=tablist]]:relative',
    '[&>button]:z-2'
  ],
  variants: {
    theme: {
      primary: ['bg-primary-200'],
      marsh: ['bg-marsh-200']
    }
  }
})

const SelectionIndicator = styled('div', {
  base: ['absolute', 'bg-white', 'rounded-md'],
  variants: {
    interacted: {
      true: ['duration-300', 'ease-in', 'transition-all'],
      false: ['transition-none']
    }
  }
})

export const SegmentedControlItemList = (
  props: React.ComponentProps<typeof Tabs.TriggerList>
): React.JSX.Element => {
  const { theme, defaultValue, value } = React.useContext(
    SegmentedControlContext
  )

  const tabsRef = React.useRef<(HTMLElement | null)[]>([])

  const selectedIndex = React.Children.toArray(props.children).findIndex(
    (child) =>
      (
        child as React.ReactElement<
          React.ComponentProps<typeof SegmentedControlItem>
        >
      )?.props.value === value
  )

  const [indicatorStyles, setIndicatorStyles] = React.useState({
    left: 0,
    height: 0,
    width: 0
  })
  const [hasInteracted, setHasInteracted] = React.useState(false)

  const updateIndicatorPosition = React.useCallback(() => {
    const currentTab = tabsRef.current[selectedIndex]
    if (!currentTab) return

    const { width, height } = currentTab.getBoundingClientRect()
    setIndicatorStyles({
      left: Math.round(currentTab.offsetLeft),
      height: Math.round(height),
      width: Math.round(width)
    })
  }, [selectedIndex])

  React.useEffect(() => {
    const resizeObserver = new ResizeObserver(updateIndicatorPosition)
    const currentTabs = tabsRef.current
    currentTabs.forEach((tab) => {
      if (tab) resizeObserver.observe(tab)
    })

    return () => {
      currentTabs.forEach((tab) => {
        if (tab) resizeObserver.unobserve(tab)
      })
      resizeObserver.disconnect()
    }
  }, [selectedIndex, updateIndicatorPosition])

  React.useEffect(() => {
    window.addEventListener('resize', updateIndicatorPosition)
    return () => {
      window.removeEventListener('resize', updateIndicatorPosition)
    }
  }, [updateIndicatorPosition])

  React.useEffect(() => {
    updateIndicatorPosition()
  }, [selectedIndex, updateIndicatorPosition])

  return (
    <StyledTriggerList theme={theme} defaultValue={defaultValue} {...props}>
      <SelectionIndicator interacted={hasInteracted} style={indicatorStyles} />
      {React.Children.map(props.children, (child, index) => {
        if (
          !React.isValidElement<
            React.ComponentProps<typeof SegmentedControlItem>
          >(child)
        ) {
          throw new Error(
            `Child passed to SegmentedControlItemList is not a valid element`
          )
        }

        return React.cloneElement(child, {
          onClick: (e) => {
            setHasInteracted(true)
            if (child.props.onClick) child.props.onClick(e)
          },
          ref: (el: HTMLElement | null) => {
            tabsRef.current[index] = el
          }
        })
      })}
    </StyledTriggerList>
  )
}
